/**
 * Global error handling configuration for Taplr application
 */
export const ErrorConfig = {
  // Toast notification settings
  toast: {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light"
  },

  // Error messages by category
  messages: {
    network: "Network error. Please check your connection and try again.",
    authentication: "Your session has expired. Please log in again.",
    authorization: "You don't have permission to perform this action.",
    validation: "Please check your input and try again.",
    server: "Server error. Please try again later.",
    general: "An unexpected error occurred. Please try again.",
    
    // Table-specific errors
    table: {
      loadData: "Failed to load table data",
      saveData: "Failed to save changes",
      deleteRow: "Failed to delete row",
      updateRow: "Failed to update row",
      dragDrop: "Failed to reorder items"
    },

    // API-specific errors
    api: {
      timeout: "Request timed out. Please try again.",
      offline: "You appear to be offline. Please check your connection.",
      rateLimit: "Too many requests. Please wait a moment and try again."
    }
  },

  // Error codes mapping
  statusCodes: {
    400: "Invalid request. Please check your input and try again.",
    401: "You need to log in to access this feature.",
    403: "You don't have permission to perform this action.",
    404: "The requested resource was not found.",
    409: "This action conflicts with existing data.",
    422: "The data provided is invalid. Please check and try again.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "Server error. Please try again later.",
    502: "Service temporarily unavailable. Please try again later.",
    503: "Service temporarily unavailable. Please try again later.",
    504: "Request timed out. Please try again later."
  },

  // Development settings
  development: {
    showDetailedErrors: true,
    logToConsole: true,
    storeErrorsInLocalStorage: true,
    maxStoredErrors: 10
  },

  // Production settings
  production: {
    showDetailedErrors: false,
    logToConsole: false,
    storeErrorsInLocalStorage: false,
    enableErrorReporting: true
  },

  // Error boundary settings
  errorBoundary: {
    fallbackComponent: 'ErrorPage',
    showReloadButton: true,
    showHomeButton: true,
    showErrorDetails: process.env.NODE_ENV === 'development'
  },

  // Retry settings
  retry: {
    maxAttempts: 3,
    baseDelay: 1000, // milliseconds
    maxDelay: 10000, // milliseconds
    backoffMultiplier: 2
  }
};

/**
 * Get current environment configuration
 */
export const getCurrentErrorConfig = () => {
  const baseConfig = ErrorConfig;
  const envConfig = process.env.NODE_ENV === 'development' 
    ? baseConfig.development 
    : baseConfig.production;
  
  return {
    ...baseConfig,
    ...envConfig
  };
};

/**
 * Get error message for HTTP status code
 */
export const getStatusCodeMessage = (statusCode) => {
  return ErrorConfig.statusCodes[statusCode] || ErrorConfig.messages.general;
};

/**
 * Check if error should be shown to user
 */
export const shouldShowErrorToUser = (error) => {
  // Don't show authentication errors in UI (handle with redirect)
  if (error?.response?.status === 401) {
    return false;
  }
  
  // Don't show network timeout errors for background operations
  if (error?.code === 'NETWORK_TIMEOUT' && error?.background) {
    return false;
  }
  
  return true;
};

export default ErrorConfig;